/**
 * Read the context Outlook hands the pane when it was opened from a card button.
 *
 * Story 26738. An ora notification email carries an Actionable Message whose
 * `Action.InvokeAddInCommand` opens THIS pane. That action can carry an
 * `initializationContext` object, and this is the only way to read it back.
 *
 * WHY THE PANE NEEDS IT AT ALL
 * ----------------------------
 * Without it the invoke still works — the pane opens on Needs You and the
 * waiting row is in the list. That fails SC-3, which asks the notification to
 * land the Servicer on THAT decision rather than on a list to hunt through. The
 * ora link already satisfies SC-3 via /workflows/decision/{id}/{key}; this is
 * how the add-in button reaches parity with it.
 *
 * WHAT IT IS NOT
 * --------------
 * Not a deep link, and not the pane's Open Email hop. `displayMessageForm`
 * (openMessage.ts) opens the ORIGINAL lender message in a new inspector and is
 * still the right call for that; it cannot open a pane, and no URL can. The
 * invoke opens the pane on the HITL message that hosts the card, and this
 * context is what tells the pane which run that card was about.
 *
 * EVERY CALLER MUST TOLERATE null. The pane is opened from the ribbon far more
 * often than from a card, and in that case the host either returns an empty
 * value or does not implement the API at all. Neither is an error.
 */

export type OraInitializationContext = {
  instanceId: string;
  decisionKey: string;
  graphMessageId: string;
};

type ContextCapableItem = {
  getInitializationContextAsync?: (
    callback: (result: { status?: unknown; value?: unknown }) => void,
  ) => void;
};

/** Coerce one field. Anything not a non-empty string is absent. */
function str(value: unknown): string {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

/**
 * Parse the raw context string. Exported so the shape is testable without a
 * host: the async plumbing above it is thin, the parsing is where the bugs are.
 */
export function parseInitializationContext(
  raw: unknown,
): OraInitializationContext | null {
  const text = str(raw);
  if (!text) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    // A host that hands back something that is not JSON is not a case worth
    // failing the pane over — the pane still opens on Needs You.
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const source = parsed as Record<string, unknown>;
  const instanceId = str(source.instanceId);
  // The instance is the only REQUIRED field: without it there is nothing to
  // open, and a context carrying only a decision key would be a card built
  // wrong rather than a card for a run we cannot find.
  if (!instanceId) return null;
  return {
    instanceId,
    decisionKey: str(source.decisionKey),
    graphMessageId: str(source.graphMessageId),
  };
}

/**
 * The context this pane was invoked with, or null.
 *
 * Never rejects and never waits forever: a host that accepts the call and never
 * calls back would otherwise hold the pane's first render behind a promise that
 * cannot settle.
 */
export function readInitializationContext(
  timeoutMs = 1500,
): Promise<OraInitializationContext | null> {
  // Read through globalThis, NOT as the bare `Office` global. Optional chaining
  // does not protect against an UNDECLARED identifier — `Office?.context` still
  // throws ReferenceError when no host injected it — and this runs on the pane's
  // first render, where the browser harness and the component tests have no
  // Office at all.
  const host = (globalThis as { Office?: { context?: { mailbox?: { item?: unknown } } } })
    .Office;
  const item = host?.context?.mailbox?.item as ContextCapableItem | undefined;
  if (typeof item?.getInitializationContextAsync !== "function") {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: OraInitializationContext | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    try {
      item.getInitializationContextAsync!((result) => {
        clearTimeout(timer);
        finish(parseInitializationContext(result?.value));
      });
    } catch {
      clearTimeout(timer);
      finish(null);
    }
  });
}
